import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenantStore } from '../stores/tenant.store';
import {
  notificationApi,
  type NotificationChannel,
  type NotificationType,
} from '../lib/api/notification.api';
import { requestAndRegisterPushToken } from '../lib/push-notifications';
import { errorText } from '../lib/error-text';

export const ALL_CHANNELS: NotificationChannel[] = ['in-app', 'email', 'push'];
// LO QUE NO ESTÁ ACÁ NO SE PUEDE APAGAR. La lista es lo que la pantalla de
// preferencias ofrece: un tipo nuevo que no se agregue queda fuera del alcance
// del usuario, que recibe un aviso semanal sin ninguna forma de pararlo.
export const ALL_NOTIFICATION_TYPES: NotificationType[] = [
  'assignment.published',
  'assignment.due-soon',
  'assignment.overdue',
  'submission.submitted',
  'submission.graded',
  'submission.returned',
  'announcement',
  'system',
];

/**
 * Shared per-user notification-preferences state + actions. Both the admin
 * (light) settings page and the portal-v2 (dark) page render their own UI on
 * top of this hook — the data logic (load / toggle / save / enable push) lives
 * here once instead of being duplicated in each page.
 */
export function useNotificationPreferences() {
  const { t } = useTranslation('notifications');
  const { currentTenantId } = useTenantStore();
  const [disabledChannels, setDisabledChannels] = useState<Set<NotificationChannel>>(new Set());
  const [optedOutTypes, setOptedOutTypes] = useState<Set<NotificationType>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!currentTenantId) return;
    setLoading(true);
    notificationApi
      .getPreferences({ tenantId: currentTenantId })
      .then((prefs) => {
        setDisabledChannels(new Set(prefs.disabledChannels));
        setOptedOutTypes(new Set(prefs.optedOutTypes));
      })
      .catch((e) => setError(errorText(e, t('prefs.alerts.loadError'))))
      .finally(() => setLoading(false));
  }, [currentTenantId, t]);

  const toggleChannel = (ch: NotificationChannel) => {
    setDisabledChannels((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch);
      else next.add(ch);
      return next;
    });
    setDirty(true);
  };

  const toggleType = (ty: NotificationType) => {
    setOptedOutTypes((prev) => {
      const next = new Set(prev);
      if (next.has(ty)) next.delete(ty);
      else next.add(ty);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    if (!currentTenantId) return;
    setSaving(true);
    setError(null);
    setSavedMsg(null);
    try {
      await notificationApi.updatePreferences({
        tenantId: currentTenantId,
        disabledChannels: Array.from(disabledChannels),
        optedOutTypes: Array.from(optedOutTypes),
      });
      setSavedMsg(t('prefs.alerts.saved'));
      setDirty(false);
    } catch (e) {
      setError(errorText(e, t('prefs.alerts.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const enablePush = async () => {
    if (!currentTenantId) return;
    setError(null);
    setSavedMsg(null);
    try {
      const token = await requestAndRegisterPushToken(currentTenantId);
      setSavedMsg(token ? t('prefs.alerts.pushRegistered') : t('prefs.alerts.pushDenied'));
    } catch (e) {
      setError(errorText(e, t('prefs.alerts.pushEnableError')));
    }
  };

  return {
    disabledChannels,
    optedOutTypes,
    loading,
    saving,
    error,
    savedMsg,
    dirty,
    toggleChannel,
    toggleType,
    save,
    enablePush,
    setError,
    setSavedMsg,
  };
}
